import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import {
  PageResponse,
  PortPointsExecution,
} from 'src/app/shared/models/migration-upload-file/migration-upload-file';
import { oneSecond } from 'src/app/utils/time';

type TStatusColorClass = {
  red: boolean;
  green: boolean;
};

@Component({
  selector: 'app-port-points-execution-head',
  templateUrl: './port-points-execution-head.component.html',
  styleUrls: ['./port-points-execution-head.component.scss'],
})
/**
 * Displays the port-points execution history and handles report downloads.
 */
export class PortPointsExecutionHeadComponent implements OnInit, OnDestroy {
  page = 0;
  pageSize = 10;

  executions: PortPointsExecution[] = [];

  loading = false;
  pulling = false;
  lastPage = false;
  hasError = false;

  call?: Subscription;
  private pollingCall?: Subscription;

  @Output() downloadReport = new EventEmitter<PortPointsExecution>();

  @Output() processingChange = new EventEmitter<boolean>();

  labels = {
    fromDate: 'from-date',
    toDate: 'to-date',
    status: 'status',
    pointsMigrated: 'points-migrated',
    triggeredAt: 'triggered-at',
    report: 'report',
    download: 'download',
    empty: 'no-port-points-executions',
    error: 'unable-to-load-port-points-executions',
  };

  /**
   * Creates the port-points execution component.
   *
   * @param {RewardService} rewardService Service used to retrieve execution
   * information.
   */
  constructor(private readonly rewardService: RewardService) {}

  /**
   * Loads the initial execution history.
   *
   * @return {void}
   */
  ngOnInit(): void {
    this.getExecutions();
  }

  /**
   * Unsubscribes from the active request and polling when the component
   * is destroyed.
   *
   * @return {void}
   */
  ngOnDestroy(): void {
    this.call?.unsubscribe();
    this.stopPolling();
  }

  /**
   * Retrieves the first page of port-points executions.
   *
   * @return {void}
   */
  getExecutions(): void {
    this.loading = true;
    this.hasError = false;
    this.page = 0;
    this.lastPage = false;

    this.call?.unsubscribe();

    this.call = this.rewardService
      .getPortPointsExecutions(this.page, this.pageSize)
      .subscribe({
        next: (response: PageResponse<PortPointsExecution>) => {
          this.executions = response.content || [];
          this.page = response.number;
          this.lastPage = response.last;

          this.updateProcessingState();
        },
        error: () => {
          this.hasError = true;
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  /**
   * Refreshes the latest port-points execution page.
   *
   * This method is called by the parent component after the manual
   * processing endpoint is triggered.
   *
   * @return {void}
   */
  refreshExecutions(): void {
    this.refreshFirstPage();
  }

  /**
   * Retrieves the first execution page and merges it with older
   * executions already loaded through infinite scrolling.
   *
   * @return {void}
   */
  private refreshFirstPage(): void {
    this.rewardService.getPortPointsExecutions(0, this.pageSize).subscribe({
      next: (response: PageResponse<PortPointsExecution>) => {
        const latestExecutions = response.content || [];

        const latestExecutionIds = new Set(
          latestExecutions.map((execution) => execution.id)
        );

        const olderExecutions = this.executions.filter(
          (execution) => !latestExecutionIds.has(execution.id)
        );

        this.executions = [...latestExecutions, ...olderExecutions];

        this.hasError = false;
        this.updateProcessingState();
      },
      error: () => {
        /*
         * Keep the existing execution history and processing state
         * when a polling request fails.
         */
      },
    });
  }

  /**
   * Retrieves and appends the next page of port-points executions.
   *
   * @return {void}
   */
  pullExecutions(): void {
    if (this.pulling || this.loading || this.lastPage) {
      return;
    }

    const nextPage = this.page + 1;
    this.pulling = true;

    this.rewardService
      .getPortPointsExecutions(nextPage, this.pageSize)
      .subscribe({
        next: (response: PageResponse<PortPointsExecution>) => {
          const newExecutions = response.content || [];

          const existingExecutionIds = new Set(
            this.executions.map((execution) => execution.id)
          );

          this.executions.push(
            ...newExecutions.filter(
              (execution) => !existingExecutionIds.has(execution.id)
            )
          );

          this.page = response.number;
          this.lastPage = response.last;

          this.updateProcessingState();
        },
        error: () => {
          this.hasError = true;
        },
      })
      .add(() => {
        this.pulling = false;
      });
  }

  /**
   * Determines whether a port-points execution is currently running,
   * emits the processing state, and starts or stops polling.
   *
   * @return {void}
   */
  private updateProcessingState(): void {
    const processing = this.executions.some(
      (execution) => execution.status === 'STARTED'
    );

    this.processingChange.emit(processing);

    if (processing) {
      this.startPolling();
    } else {
      this.stopPolling();
    }
  }

  /**
   * Starts polling the first execution page every 30 seconds.
   *
   * Polling starts only while an execution has the STARTED status.
   *
   * @return {void}
   */
  private startPolling(): void {
    if (this.pollingCall) {
      return;
    }

    this.pollingCall = interval(30 * oneSecond).subscribe(() => {
      this.refreshFirstPage();
    });
  }

  /**
   * Stops execution-status polling.
   *
   * @return {void}
   */
  private stopPolling(): void {
    this.pollingCall?.unsubscribe();
    this.pollingCall = undefined;
  }

  /**
   * Returns the CSS status classes for an execution status.
   *
   * @param {string} status Execution status.
   * @return {TStatusColorClass} Status color classes.
   */
  executionStatusClass(status?: string): TStatusColorClass {
    return {
      green: status === 'SUCCESS',
      red: status === 'FAILED',
    };
  }

  /**
   * Determines whether an execution report is available for download.
   *
   * @param {PortPointsExecution} execution Port-points execution.
   * @return {boolean} True when the execution contains a report file name.
   */
  canDownloadReport(execution: PortPointsExecution): boolean {
    return Boolean(execution.reportFileName);
  }

  /**
   * Emits the selected execution when its report is available.
   *
   * @param {PortPointsExecution} execution Port-points execution selected for
   * download.
   * @return {void}
   */
  emitDownloadReport(execution: PortPointsExecution): void {
    if (this.canDownloadReport(execution)) {
      this.downloadReport.emit(execution);
    }
  }
}
